import AgeRestrictionDropdown from "components/dropdowns/reactselect/AgeRestrictionDropdown";
import { FormSection } from "components/MainSectionTemplate/types";
import VenueModalPicker from "components/Picker/VenuePicker/VenueModalPicker";
import OptionalNumberInput from "components/ValidatedFields/OptionalNumberInput";
import { DoorsEdit } from "company/detail/components/DoorsEdit";
import { DoorsFormState } from "shared/utils/fieldsHelper/companyFields";
import React from "react";
import ReadonlyVenue from "show/detail/components/ReadonlyVenue";
import ReadonlyContact from "show/detail/components/ReadonlyContact";
import { fetchFullContact } from "show/detail/utils";
import { getInstructionForInternal, ShowFieldMetaData, ShowFormState } from "shared/utils/fieldsHelper/showFields";
import { getAgeRestriction } from "utilities/enumutils";
import ContactModalPicker from "components/Picker/ContactPicker/ContactModalPicker";
import Doors from "venue/components/Doors";

export const venueSection = (
  show: server.dto.Show,
  showInfo: ShowFormState,
  venueContact: server.dto.Contact,
  isExternal: boolean,
  fieldMetaData: ShowFieldMetaData,
  isSidebar: boolean
): FormSection => {
  const isInternalUser = !isExternal;
  const ageRestriction = showInfo.ageRestriction ? getAgeRestriction(showInfo.ageRestriction) : null;

  const section: FormSection = {
    type: "regular",
    key: "showVenueSection",
    title: site.currentEnvironment.functionGroups.venue.singular,
    hidden: !site.currentEnvironment.functionGroups.venue.enabled,
    fields: [{
      ...fieldMetaData.venueCompany.fields(),
      key: "venue",
      label: site.currentEnvironment.functionGroups.venue.singular,
      render: () => <ReadonlyVenue venue={showInfo.venue} displayTooltip={isInternalUser} />,
      inPageEdit: {
        hoverEdit: true,
        canRemove: true,
        initialValue: showInfo.venue,
        fullWidth: true,
        render: ({ onChange, cancel, value }) => (
          <div className="main-section-template-fields-padding">
            <VenueModalPicker
              inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
              canCreateNew={!isSidebar && site.permissions.addressBook.canCreate}
              afterCreate={isSidebar ? "goBack" : "closeSidebar"}
            />
          </div>
        ),
        type: "custom",
        submitChange: async (value: server.dto.VenueSmall) => {
          if (value == null) {
            return window.CoreApi.Show.UpdateForInternalEnvironment(
              getInstructionForInternal(show.showId, showInfo, {
                venueCompanyId: null,
                venueContactId: null,
              })
            );
          }
          const venue = await window.CoreApi.Venue.GetVenue(value.venueId);
          const { details } = venue.data.data;

          return window.CoreApi.Show.UpdateForInternalEnvironment(
            getInstructionForInternal(show.showId, showInfo, {
              venueCompanyId: value?.venueId ?? null,
              venueContactId: null,
              capacity: details.capacity,
              ageRestriction: details.ageRestriction,
              doors: {
                open: details.doors?.open?.cultureInvariant,
                close: details.doors?.close?.cultureInvariant,
              }

            })
          )
        }
      }
    }, {
      ...fieldMetaData.venueContact.fields(),
      key: "venueContact",
      label: RESX.Contact.resxContact,
      render: () => <ReadonlyContact contact={venueContact} displayDetails displayPreview={isInternalUser} />,
      inPageEdit: {
        initialValue: venueContact,
        fullWidth: true,
        hoverEdit: true,
        canRemove: true,
        render: ({ onChange, cancel, value }) => (
          <div className="main-section-template-fields-padding">
            <ContactModalPicker
              inPageEditing={{
                onChange: async (picked: server.dto.ContactSmallV2, submit: boolean) => {
                  const full = await fetchFullContact(picked);
                  onChange(full, submit);
                },
                cancel,
                hasValue: Boolean(value),
              }}
              fromCompanyId={showInfo.venue?.venueId}
              canCreateNew={!isSidebar && site.permissions.addressBook.canCreate}
              afterCreate={isSidebar ? "goBack" : "closeSidebar"}
            />
          </div>
        ),
        type: "custom",
        submitChange: (value: server.dto.Contact) =>
          window.CoreApi.Show.UpdateForInternalEnvironment(
            getInstructionForInternal(show.showId, showInfo, { venueContactId: value?.contactId ?? null })
          )
      }
    }, {
      ...fieldMetaData.capacity.fields(),
      key: "capacity",
      label: RESX.Venue.Capacity,
      render: () => <>{showInfo.capacity} PAX</>,
      inPageEdit: {
        initialValue: showInfo.capacity,
        render: ({ value, onChange, cancel }) => (
          <OptionalNumberInput
            autoFocus
            value={value}
            onChange={onChange}
            onEnterPressed={v => onChange(v, true)}
            onEscPressed={cancel}
            minValue={0}
            maxValue={serverReference.constants.venue.maxCapacity}
            canBeEmpty
            decimalPoints={0}
            labelContent="PAX"
            width={100}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: (value: number) =>
          window.CoreApi.Show.UpdateForInternalEnvironment(
            getInstructionForInternal(show.showId, showInfo, { capacity: value ?? 0 })
          )
      }
    }, {
      ...fieldMetaData.ageRestriction.fields(),
      key: "ageRestriction",
      label: RESX.Venue.AgeRestrictions,
      render: () => <>{ageRestriction?.text}</>,
      inPageEdit: {
        initialValue: ageRestriction,
        fullWidth: true,
        render: ({ value, onChange }) => (
          <AgeRestrictionDropdown
            value={value}
            onChange={onChange}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: (value: server.dto.AgeRestriction) =>
          window.CoreApi.Show.UpdateForInternalEnvironment(
            getInstructionForInternal(show.showId, showInfo, { ageRestriction: value.value })
          )
      }
    }, {
      ...fieldMetaData.doors.fields(),
      key: "doors",
      label: RESX.Venue.Doors,
      render: () => <Doors doors={showInfo.doors} />,
      inPageEdit: {
        initialValue: showInfo.doors,
        render: ({ value, onChange }) => (
          <DoorsEdit
            value={value}
            onChange={onChange}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: (value: DoorsFormState) =>
          window.CoreApi.Show.UpdateForInternalEnvironment(
            getInstructionForInternal(show.showId, showInfo, {
              doors: {
                open: value.open?.format("HH:mm") ?? null,
                close: value.close?.format("HH:mm") ?? null,
              }
            })
          )
      }
    }]
  };

  return section;
};
